/**
 * Routes streaming chunk events to per-chat state.
 *
 * Every chunk from the server carries a `chat_id`. The router applies the chunk
 * to that chat's state — never the global or another chat's — which makes
 * bleed-over impossible at the client boundary.
 *
 * Used by the SSE reader (Phase 2) and the WS chunk handler (Phase 3).
 */

import {
  chatStreamStates,
  getChatStream,
  updateChatStream,
  clearChatStream,
} from './chat-state';
import type { ChatMessage } from '../../core/api/types';

export interface ChunkEvent {
  type: 'chunk' | 'result' | 'done' | 'error';
  chat_id: number;
  seq?: number;
  content?: string;
  mode?: 'replace' | 'delta';
  done?: boolean;
  turn_id?: string;
}

/**
 * Append an assistant message to the history for a given chat.
 * Injected at module init by chat-store (avoids circular import).
 */
type AppendAssistant = (chatId: number, content: string) => void;
let _appendAssistant: AppendAssistant | null = null;

export function setAssistantAppender(fn: AppendAssistant): void {
  _appendAssistant = fn;
}

/**
 * Fires after a chat finalizes (done or error). Used by chat-store to drain
 * the pending-message queue for that chat. Injected to avoid circular imports.
 */
type FinalizeHook = (chatId: number, turnId: string | null) => void;
let _streamFinalized: FinalizeHook | null = null;

export function setStreamFinalized(fn: FinalizeHook): void {
  _streamFinalized = fn;
}

/**
 * Route a single chunk event to its chat's state.
 *
 * Rules:
 *   - Chunks without a chat_id are ignored (client-side defense in depth).
 *   - Replayed chunks (seq <= lastSeq) are ignored — idempotent.
 *   - `done` finalizes: flushes content to history, clears stream state.
 *   - `result` updates content only when longer than accumulated text
 *     (tool-use responses send result as the LAST text block, not total).
 *   - `chunk` with mode='delta' appends; mode='replace' replaces if at least
 *     as long as what we have.
 */
export function routeChunk(evt: ChunkEvent): void {
  const chatId = evt.chat_id;
  if (chatId == null) return;

  const state = getChatStream(chatId);

  if (evt.turn_id && state.turnId && evt.turn_id !== state.turnId) return;
  if (evt.turn_id && !state.turnId) {
    updateChatStream(chatId, { turnId: evt.turn_id });
  }

  if (evt.seq != null && evt.seq <= state.lastSeq) return;

  if (state.streaming) updateChatStream(chatId, { lastActivityAt: Date.now() });

  if (evt.type === 'done' || evt.done) {
    if (evt.seq != null) updateChatStream(chatId, { lastSeq: evt.seq });
    // A successful terminal frame proves the turn survived any provisional
    // transport interruption that happened earlier.
    if (state.interruptionKind === 'transport') updateChatStream(chatId, {
      interrupted: false,
      interruptionKind: null,
      activity: '',
    });
    finalizeChat(chatId, !getChatStream(chatId).interrupted);
    return;
  }

  if (evt.type === 'error') {
    // Error carries visible detail, but `done` is the only terminal boundary.
    // Draining the queue here lets the prior turn's trailing done frame erase
    // the newly-started follow-up because frames do not yet carry turn IDs.
    updateChatStream(chatId, {
      streaming: state.interruptionKind === 'turn_lost' ? false : true,
      interrupted: true,
      interruptionKind: state.interruptionKind === 'turn_lost' ? 'turn_lost' : 'error',
      activity: evt.content || 'Request failed',
      lastActivityAt: Date.now(),
      lastSeq: evt.seq ?? state.lastSeq,
    });
    return;
  }

  if (evt.type === 'result' && evt.content != null) {
    // The gateway result is the canonical final body. Progress previews and
    // earlier assistant blocks can be longer than the final answer.
    updateChatStream(chatId, {
      content: evt.content,
      lastSeq: evt.seq ?? state.lastSeq,
      lastActivityAt: Date.now(),
    });
    return;
  }

  if (evt.type === 'chunk' && evt.content != null) {
    const nextContent =
      evt.mode === 'replace'
        ? (evt.content.length >= state.content.length
            ? evt.content
            : state.content)
        : state.content + evt.content;
    updateChatStream(chatId, {
      streaming: true,
      content: nextContent,
      lastSeq: evt.seq ?? state.lastSeq,
      startedAt: state.startedAt ?? Date.now(),
      lastActivityAt: Date.now(),
    });
  }
}

function finalizeChat(chatId: number, saveContent = true): void {
  const state = getChatStream(chatId);
  const turnId = state.turnId;
  const content = state.content;
  const interrupted = state.interrupted;
  const interruptionKind = state.interruptionKind;
  const activity = state.activity;
  const lastSeq = state.lastSeq;
  const startedAt = state.startedAt;
  if (saveContent && content && _appendAssistant) {
    _appendAssistant(chatId, content);
  }
  clearChatStream(chatId);
  if (interrupted) updateChatStream(chatId, {
    streaming: false,
    interrupted: true,
    interruptionKind,
    activity,
    lastSeq,
    startedAt,
  });
  if (_streamFinalized) _streamFinalized(chatId, turnId);
}

/** Begin a stream for a chat: seed streaming=true and reset content. */
export function beginStream(
  chatId: number,
  abortController: AbortController | null,
): void {
  const previousLastSeq = getChatStream(chatId).lastSeq;
  updateChatStream(chatId, {
    turnId: null,
    provider: '',
    model: '',
    phase: '',
    streaming: true,
    content: '',
    startedAt: Date.now(),
    lastSeq: previousLastSeq,
    abortController,
    queuedChars: 0,
    activity: '',
    progress: '',
    progressBlocks: [],
    elapsedSeconds: null,
    elapsedReceivedAt: null,
    toolCalls: null,
    lastTool: '',
    lastActivityAt: Date.now(),
    lastHeartbeatAt: Date.now(),
    interrupted: false,
    interruptionKind: null,
  });
}

/** Test helper: reset all per-chat state. */
export function __resetAllChatStreamsForTests(): void {
  chatStreamStates.value = {};
}

/** Appends a content string to a chat's history via the registered appender. */
export function appendAssistantMessage(
  chatId: number,
  content: string,
  appendFn: (msg: ChatMessage) => void,
): void {
  const msg: ChatMessage = {
    role: 'assistant',
    content,
    timestamp: new Date().toISOString(),
  };
  appendFn(msg);
}
